"use client";
import React, {
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  useId,
} from "react";
import { useFormGroup } from "../FormGroup";
import { formControlVariants, FormControlVariantsProps } from "../classes";
import { useInputGroupContext } from "./InputGroup";
import { cn } from "../../../utils/cn";

export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>,
    FormControlVariantsProps {
  className?: string;
  error?: boolean;
  prefixAdornments?: () => ReactNode;
  onEnterPress?: (value: string) => void;
  "data-testid"?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    className,
    onEnterPress,
    onKeyUp,
    error,
    vSize,
    vColor,
    type,
    "data-testid": dataTestId,
    ...props
  },
  ref,
) {
  const formGroup = useFormGroup();
  if (error == null) {
    error = !!formGroup?.controller?.fieldState.error;
  }
  const inputGroup = useInputGroupContext();
  // to cater for left/right addons
  const paddingLeft = inputGroup?.leftWidth
    ? `${inputGroup.leftWidth + 10}px`
    : undefined;
  const paddingRight = inputGroup?.rightWidth
    ? `${inputGroup.rightWidth + 10}px`
    : undefined;

  function _onKeyUp(evt: React.KeyboardEvent<HTMLInputElement>) {
    onKeyUp && onKeyUp(evt);
    if (evt.key === "Enter") {
      onEnterPress && onEnterPress(evt.currentTarget.value);
    }
  }

  // Use React's useId for SSR-safe unique IDs
  const reactId = useId();
  const safeId = reactId.replace(/:/g, "");

  // Auto-generate data-testid from name or id if not provided
  const generateInputTestId = () => {
    if (dataTestId) return dataTestId;
    if (props.name) return `input-${props.name}-${safeId}`;
    if (formGroup?.id) return `input-${formGroup.id}-${safeId}`;
    return `input-${safeId}`;
  };

  const testId = generateInputTestId();

  const css = cn(
    formControlVariants({
      vSize,
      vColor: vColor ? vColor : error ? "error" : undefined,
    }),
    "w-full",
    inputGroup && "grow",
    className,
  );
  return (
    <input
      className={css}
      id={formGroup?.id}
      onKeyUp={_onKeyUp}
      ref={ref}
      style={{ paddingLeft, paddingRight }}
      type={type || "text"}
      data-testid={testId}
      {...props}
    />
  );
});
